import { useQueryDecoratedSiloWithSuspense } from '../../../queries/decoratedSilo';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { goTo, PATH, siloPageAssetQueryStringKey } from 'app/hooks/useRoute';
import { Screen } from './shared';
import { updateURLSearchParams } from 'app/functions/url-search-params';
import { useSiloWeb3 } from '@silo/web3';
import { ChecksumAddress, getChecksumAddressOrThrow } from '@silo/types';
import { findProtocolInstanceForMarket } from '../../../queries/markets/fallbackMarkets';

/**
 * @deprecated use `useThisSilo` instead. This hook doesn't make requests to the graph
 */
export const useThisDecoratedSilo = () => {
  const siloAddress = useThisSiloAddress();
  const decoratedSilo = useQueryDecoratedSiloWithSuspense(siloAddress);

  return decoratedSilo;
};

/**
 * Returns the silo address parsed from the URL, or null if absent/invalid.
 * Validity is enforced by `useValidateSiloAddress`, which runs on the same
 * page and redirects to `/dashboard` when the address is not a known silo.
 */
export const useThisSiloAddress = () => useRouterQueryAddress() ?? null;

/**
 * Validates the URL-provided silo address against the static markets blob.
 * Previously this issued an on-chain `SiloRepository.isSilo` probe across
 * every runtime protocol instance — pure RPC waste, since the static blob
 * already enumerates every deployed silo we support. Synchronous lookup now.
 */
export const useValidateSiloAddress = () => {
  const { protocolInstance, setProtocolInstance } = useSiloWeb3();
  const shouldSyncProtocolInstance = useRef(true);
  const router = useRouter();
  const address = useRouterQueryAddress();

  // if no address, redirect to the dashboard
  useEffect(() => {
    if (!address && router.isReady) goTo(PATH.DASHBOARD);
  }, [router.isReady, address]);

  const siloProtocolInstance = useMemo(
    () => (address ? findProtocolInstanceForMarket(address) : null),
    [address]
  );

  const isInvalid = !!address && router.isReady && siloProtocolInstance === null;

  // if address is not a valid silo, redirect to the dashboard
  useEffect(() => {
    if (isInvalid) {
      goTo(PATH.DASHBOARD);
    }
  }, [isInvalid]);

  useEffect(() => {
    if (address) {
      shouldSyncProtocolInstance.current = true;
    }
  }, [address]);

  /**
   * If silo is on a different protocol instance, attempt to switch
   */
  useEffect(() => {
    if (!protocolInstance || !siloProtocolInstance) {
      // noop - wait until these have been determined to make an action
      return;
    }

    if (protocolInstance === siloProtocolInstance) {
      // the silo protocol instance matches the currently selected protocol instance: noop
      shouldSyncProtocolInstance.current = false;
      return;
    }

    if (shouldSyncProtocolInstance.current) {
      setProtocolInstance(siloProtocolInstance, true);
      shouldSyncProtocolInstance.current = false;
    } else {
      goTo(PATH.DASHBOARD);
    }
  }, [protocolInstance, siloProtocolInstance, setProtocolInstance]);
};

const useRouterQueryAddress = () => {
  const router = useRouter();
  const address = useMemo<ChecksumAddress | undefined>(() => {
    const queryAddress = router.query.address;

    try {
      if (Array.isArray(queryAddress) && queryAddress.length > 0 && typeof queryAddress[0] === 'string') {
        return getChecksumAddressOrThrow(queryAddress[0]);
      }
      if (typeof queryAddress === 'string') {
        return getChecksumAddressOrThrow(queryAddress);
      }
    } catch (error) {
      return;
    }
  }, [router.query]);

  return address;
};

export const useClearAssetFromQueryString = (screen: Screen) => {
  const router = useRouter();

  return useCallback(() => {
    const newPath = updateURLSearchParams((params) => {
      params.delete(siloPageAssetQueryStringKey(screen));
      return params;
    })(router.asPath);

    router.replace(newPath, undefined, {
      shallow: true,
    });
  }, [router, screen]);
};
